import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {Config} from "../config";
import axios from "axios";

let peerConnection = null;
let dataChannel = null;
let mediaStream = null;
let transceiver = null;

// --- Streaming delta batching ----------------------------------------------
// Token deltas arrive very frequently; applying each one immediately re-renders
// the whole message (markdown re-parse) and runs the autoscroll on every token.
// The batcher coalesces deltas into ~DELTA_FLUSH_MS chunks so text appears in
// smooth bursts with far fewer renders/scrolls. Deltas are keyed by response_id
// in an insertion-ordered Map, so it stays correct and ordered even if more than
// one response ever streams at once.
const DELTA_FLUSH_MS = 100;

function createDeltaBatcher(flushMs) {
    let pending = new Map(); // response_id -> { type, text }
    let timer = null;

    const flush = (dispatch) => {
        if (timer !== null) {
            clearTimeout(timer);
            timer = null;
        }
        if (pending.size === 0) return;
        const batch = pending;
        pending = new Map();
        batch.forEach(({ type, text }, id) => {
            if (text) dispatch(handleEvent({ type, response_id: id, delta: text }));
        });
    };

    return {
        // Buffer a streaming delta and make sure a flush is scheduled.
        push(event, dispatch) {
            const entry = pending.get(event.response_id) || { type: event.type, text: '' };
            entry.text += event.delta || '';
            pending.set(event.response_id, entry);
            if (timer === null) {
                timer = setTimeout(() => { timer = null; flush(dispatch); }, flushMs);
            }
        },
        // Emit everything buffered so far, immediately and in arrival order.
        flush,
        // Drop any pending batch (on new connection / teardown).
        reset() {
            if (timer !== null) {
                clearTimeout(timer);
                timer = null;
            }
            pending = new Map();
        },
    };
}

const deltaBatcher = createDeltaBatcher(DELTA_FLUSH_MS);

/**
 * Creates a WebRTC peer connection with audio transceivers, data channel, and SDP offer.
 *
 * @param {Function} dispatch - Redux dispatch function
 * @returns {Promise<string>} SDP offer
 */
async function createPeerConnection(dispatch) {
    deltaBatcher.reset();
    peerConnection = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    });

    const audioEl = document.createElement("audio");
    audioEl.autoplay = true;
    peerConnection.addEventListener("track", async (e) => {
        audioEl.srcObject = e.streams[0];
    });

    peerConnection.addTransceiver("audio", { direction: "recvonly" });
    transceiver = peerConnection.addTransceiver("audio", { direction: "sendonly" });

    dataChannel = peerConnection.createDataChannel("oai-events", {});
    dataChannel.addEventListener("message", (e) => {
        const event = JSON.parse(e.data);
        // Coalesce high-frequency token deltas; flush every other event
        // immediately (flushing any pending deltas first so visible text stays
        // ordered, e.g. before response.text.done sets the final parsed text).
        if (event.type === 'response.text.delta' || event.type === 'response.audio_transcript.delta') {
            deltaBatcher.push(event, dispatch);
            return;
        }
        deltaBatcher.flush(dispatch);
        dispatch(handleEvent(event));
    });
    dataChannel.addEventListener("error", (e) => {
        console.error("Data channel error:", e);
    });
    dataChannel.addEventListener('close', () => {
        dispatch(disconnectChat());
    });

    const offer = await peerConnection.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: false,
    });
    await peerConnection.setLocalDescription(offer);

    return offer.sdp;
}

/**
 * Cleans up the peer connection, data channel, and transceiver.
 */
function cleanupPeerConnection() {
    deltaBatcher.reset();
    if (peerConnection) {
        try { peerConnection.close(); } catch (_) {}
    }
    peerConnection = null;
    dataChannel = null;
    transceiver = null;
}


/**
 * Parses <<options:Option1|Option2>> markers from AI message text.
 * The model is instructed to emit at most one such tag, but it occasionally
 * emits several. Merge labels from every tag (deduped, preserving order) and
 * strip ALL occurrences from the visible text.
 * Returns { cleanText, options } where cleanText has the markers stripped.
 */
function parseReplyOptions(text) {
    const options = [];
    const regex = /<<options:(.+?)>>/g;
    let match;
    while ((match = regex.exec(text)) !== null) {
        match[1].split('|').forEach(o => {
            const trimmed = o.trim();
            if (trimmed && options.indexOf(trimmed) === -1) {
                options.push(trimmed);
            }
        });
    }
    const cleanText = text.replace(/\s*<<options:.+?>>/g, '').trim();
    return { cleanText, options };
}

/**
 * Parses <<rating>> marker from AI message text.
 * Returns { cleanText, showRating } where cleanText has the marker stripped.
 */
function parseRating(text) {
    const hasRating = /<<rating>>/.test(text);
    const cleanText = text.replace(/\s*<<rating>>/, '').trim();
    return { cleanText, showRating: hasRating };
}

/**
 * Chat slice for managing chat state and actions.
 */
// LaDesk hands touch devices a chat it opens with window.open(), and Safari only
// permits that while a tap is being handled. The assistant's escalation arrives over
// the data channel with no tap behind it, so on iPhone the window is blocked and
// nothing appears. Tablets are usually served the in-page chat and open fine, so
// rather than guess which is which we do both there: fire the event as always, and
// also offer a button the visitor can tap if nothing came up.
const ESCALATION_MAY_NEED_TAP = typeof navigator !== 'undefined'
    && (navigator.maxTouchPoints > 0 || 'ontouchstart' in window);

export const chatSlice = createSlice({
    name: 'chat',
    initialState: {
        messages: [],
        loading: true,
        isOpen: false,
        withAudio: false,
        audioEnabled: false,
        chatStarted: false,
        error: null,
        available: false,
        inlineMode: false,
        sidebarMode: false,
        context: null,
        suggestions: [],
        suggestionsContextLabel: '',
        suggestionsLoading: false,
        replyOptions: [],
        showRating: false,
        chatRatingNegative: false,
        chatRatingNegativeSinceIndex: null,
        pendingCallSupport: false,
    },
    reducers: {
        applyTheme: (state, action) => {
            let config = Config.getInstance();
            if (config.theme) {
                Object.keys(config.theme).forEach(key => {
                    document.documentElement.style.setProperty(`--${key}`, config.theme[key]);
                });
            }
        },
        setInlineMode: (state, action) => {
            let config = Config.getInstance();
            state.inlineMode = config.mode === 'inline';
            state.sidebarMode = config.mode === 'sidebar';
        },
        toggleSidebarMode: (state) => {
            state.sidebarMode = !state.sidebarMode;
        },
        setContext: (state, action) => {
            state.context = action.payload;
            state.suggestions = [];
            state.suggestionsContextLabel = '';
            state.suggestionsLoading = false;
        },
        setMessageRating: (state, action) => {
            const msg = state.messages.find(m => m.dbId === action.payload.dbId);
            if (msg) {
                msg.rating = action.payload.rating;
            }
        },
        setChatRatingNegative: (state, action) => {
            const { negative, sinceIndex } = action.payload;
            state.chatRatingNegative = negative;
            state.chatRatingNegativeSinceIndex = negative ? sinceIndex : null;
        },
        postMessage: (state, action) => {
            if (!dataChannel || dataChannel.readyState !== 'open') return;

            const message = {
                type: "user.message",
                message: action.payload.message
            };

            dataChannel.send(JSON.stringify(message));

            state.messages.push({
                'id': Math.random(),
                'sender': 'client',
                'text': action.payload.message,
                'status': 'done',
                'type': 'text',
            });

            state.replyOptions = [];
            state.showRating = false;
        },
        handleEvent: (state, action) => {
            switch (action.payload.type) {
                case 'session.created':
                    state.loading = false;
                    if (action.payload.session?.id) {
                        sessionStorage.setItem('ai_chat_session_id', action.payload.session.id);
                    }
                    break;
                case 'conversation.function.call':
                    if (action.payload.item?.type === 'function_call') {
                        state.messages.push({
                            'id': action.payload.item.id,
                            'sender': 'bot',
                            'type': 'thinking',
                            'status': 'thinking'
                        });
                    }
                    if (action.payload.item?.type === 'function_call_output') {
                        let idx = state.messages.findIndex(item => item.id === action.payload.item.id);
                        if (idx !== -1) {
                            state.messages.splice(idx, 1);
                        }
                    }
                    break;
                case 'function.executed':
                    if (action.payload.context) {
                        window.dispatchEvent(new CustomEvent('sb:external:update', {
                            detail: action.payload.context
                        }));
                    }
                    break;
                case 'action.call_support':
                    window.dispatchEvent(new CustomEvent('sb:call_support'));
                    if (ESCALATION_MAY_NEED_TAP) {
                        // Put the button on the reply that explains the escalation, so
                        // it reads as part of the conversation. The tool call normally
                        // lands before that reply exists, so remember it for the next
                        // one; if the assistant spoke first, mark what it just said.
                        const spoken = state.messages[state.messages.length - 1];

                        if (spoken && spoken.sender === 'bot' && spoken.type === 'text') {
                            spoken.call_support = true;
                        } else {
                            state.pendingCallSupport = true;
                        }
                    }
                    break;
                case 'response.output_item.added':
                    let res = {
                        //'id': action.payload.item.id,
                        'id': action.payload.response_id,
                        'sender': 'bot',
                        'text': '',
                        'status': 'in_progress',
                        'type': 'text',
                    };

                    if (action.payload.payment_link) {
                        res.payment_link = action.payload.payment_link;
                    }
                    if (state.pendingCallSupport) {
                        res.call_support = true;
                        state.pendingCallSupport = false;
                    }
                    state.messages.push(res);

                    break;
                case 'response.text.delta':
                case 'response.audio_transcript.delta':
                    const message = state.messages.find(m => m.id === action.payload.response_id);
                    if (message) {
                        message.text += action.payload.delta;
                    }
                    break;
                case 'response.text.done':
                case 'response.audio_transcript.done':
                    const completedMessage = state.messages.find(m => m.id === action.payload.response_id);
                    if (completedMessage) {
                        completedMessage.status = 'done';
                        const rawText = action.payload.text ?? action.payload.transcript;
                        const { cleanText: cleanText1, showRating } = parseRating(rawText);
                        const { cleanText, options } = parseReplyOptions(cleanText1);
                        completedMessage.text = cleanText;
                        if (options.length > 0) {
                            state.replyOptions = options;
                        }
                        if (showRating) {
                            state.showRating = true;
                        }
                    }
                    break;
                case 'message.db_id': {
                    const msg = state.messages.find(m => m.id === action.payload.response_id);
                    if (msg) {
                        msg.dbId = action.payload.db_id;
                    }
                    break;
                }
                case 'session.restored':
                    if (action.payload.messages && action.payload.messages.length > 0) {
                        action.payload.messages.forEach(msg => {
                            state.messages.push({
                                'id': Math.random(),
                                'sender': msg.role === 'user' ? 'client' : 'bot',
                                'text': msg.text,
                                'status': 'done',
                                'type': msg.type || 'text',
                                'dbId': msg.dbId || null,
                                'rating': msg.rating || null,
                            });
                        });

                        // Restore reply options / rating prompt from the most recent
                        // assistant message — only if the user hasn't replied since.
                        const last = action.payload.messages[action.payload.messages.length - 1];
                        if (last && last.role !== 'user') {
                            if (Array.isArray(last.options) && last.options.length > 0) {
                                state.replyOptions = last.options;
                            }
                            if (last.showRating) {
                                state.showRating = true;
                            }
                        }
                    }
                    if (typeof action.payload.chatRatingNegative === 'boolean') {
                        state.chatRatingNegative = action.payload.chatRatingNegative;
                        // Backend doesn't track which restored message the rating started at,
                        // so a restored negative rating applies to the whole restored history.
                        state.chatRatingNegativeSinceIndex = action.payload.chatRatingNegative ? 0 : null;
                    }
                    break;
                case 'search.results': {
                    const msgWithSearch = state.messages.find(m => m.id === action.payload.response_id);
                    if (msgWithSearch) {
                        const results = action.payload.results || {};
                        const links = [];
                        ['admin', 'help_center', 'youtube', 'other'].forEach(key => {
                            if (results[key] && results[key].matches) {
                                const matches = results[key].matches;
                                const keys = Object.keys(matches);
                                for (let i = 0; i < keys.length; i++) {
                                    const item = matches[keys[i]];
                                    const merged = Object.assign({}, item, item.metadata || {});
                                    if (merged.title && merged.url) {
                                        links.push({
                                            category: key,
                                            title: merged.title,
                                            url: merged.url
                                        });
                                    }
                                }
                            }
                        });
                        if (links.length > 0) {
                            msgWithSearch.searchResults = links;
                        }
                    }
                    break;
                }
                case 'input_audio_buffer.speech_started':
                    state.messages.push({
                        'id': action.payload.item_id,
                        'sender': 'client',
                        'status': 'in_progress',
                        'type': 'audio',
                    });
                    break;
                case 'input_audio_buffer.speech_stopped':
                    const completedAudio = state.messages.find(m => m.id === action.payload.item_id);
                    if (completedAudio) {
                        completedAudio.status = 'done';
                    }
                    break;
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(initChat.fulfilled, (state, action) => {
            state.messages = [];
            state.isOpen = true;
            state.chatStarted = true;
            state.audioEnabled = action.payload.audioEnabled;
        });
        builder.addCase(initChat.rejected, (state, action) => {
            state.loading = true;
            state.error = action.payload.error || "Unknown error occurred during chat initialization. Please try again later.";
        });
        builder.addCase(initChat.pending, (state, action) => {
            state.loading = true;
        });
        builder.addCase(enableAudio.fulfilled, (state, action) => {
            state.withAudio = true;
        });
        builder.addCase(enableAudio.rejected, (state, action) => {
            state.withAudio = false;
        });
        builder.addCase(disableAudio.fulfilled, (state, action) => {
            state.withAudio = false;
        });
        builder.addCase(disconnectChat.fulfilled, (state, action) => {
            state.withAudio = false;
            state.isOpen = false;
            state.error = null;
        });
        builder.addCase(disconnectChat.rejected, (state, action) => {
            console.error("Failed to disconnect chat:", action.error);
            state.withAudio = false;
            state.isOpen = false;
            state.error = null;
        })
        builder.addCase(closeChat.fulfilled, (state, action) => {
            state.messages = [];
            state.chatStarted = false;
            state.error = null;
            state.showRating = false;
        });
        builder.addCase(closeChat.rejected, (state, action) => {
            console.error("Failed to close chat:", action.error);
            state.messages = [];
            state.chatStarted = false;
            state.error = null;
            state.showRating = false;
        })
        builder.addCase(startNewChat.pending, (state) => {
            state.messages = [];
            state.loading = true;
            state.showRating = false;
        })
        builder.addCase(checkAvailability.fulfilled, (state, action) => {
            state.available = action.payload.available;
        })
        builder.addCase(checkAvailability.rejected, (state, action) => {
            state.available = false;
        })
        builder.addCase(fetchSuggestions.pending, (state) => {
            state.suggestionsLoading = true;
        })
        builder.addCase(fetchSuggestions.fulfilled, (state, action) => {
            state.suggestions = action.payload.suggestions || [];
            state.suggestionsContextLabel = action.payload.context_label || '';
            state.suggestionsLoading = false;
        })
        builder.addCase(fetchSuggestions.rejected, (state) => {
            state.suggestions = [];
            state.suggestionsContextLabel = '';
            state.suggestionsLoading = false;
        })
    },
})

/**
 * Initializes the chat session by creating a WebRTC peer connection and data channel.
 */
export const initChat = createAsyncThunk('chat.init', async (data, thunkAPI) => {
    if (peerConnection) {
        return;
    }

    // Any path that actually starts (or restarts) the chat invalidates the
    // "user explicitly closed" marker. Without this, the auto-init effect
    // would still see the flag and refuse to reconnect on the next page load.
    sessionStorage.removeItem('ai_chat_closed_by_user');

    const config = Config.getInstance();

    // Try to reconnect to an existing hibernating bridge first
    const prevSessionId = sessionStorage.getItem('ai_chat_session_id');
    if (prevSessionId) {
        try {
            const sdpOffer = await createPeerConnection(thunkAPI.dispatch);

            const reconnectResponse = await fetch(config.rtc_host + '/webrtc/reconnect', {
                method: "POST",
                body: JSON.stringify({ sessionId: prevSessionId, sdpOffer }),
                headers: { "Content-Type": "application/json" },
            });

            if (reconnectResponse.ok) {
                const reconnectJson = await reconnectResponse.json();
                await peerConnection.setRemoteDescription({
                    type: "answer",
                    sdp: reconnectJson.sdpAnswer,
                });
                return { audioEnabled: reconnectJson.audioEnabled };
            }

            // Reconnect failed — clean up and fall through to normal init
            cleanupPeerConnection();
            sessionStorage.removeItem('ai_chat_session_id');
        } catch (e) {
            cleanupPeerConnection();
            sessionStorage.removeItem('ai_chat_session_id');
        }
    }

    // Normal init flow: create new session
    let token = '';
    try {
        let res = await axios.post(config.host + '/v2/chat-widget-api/init-token');
        token = res.data.token;
    } catch (err) {
        return thunkAPI.rejectWithValue({
            error: "Failed to initialize chat session."
        });
    }

    const sdpOffer = await createPeerConnection(thunkAPI.dispatch);

    const sdpResponse = await fetch(config.rtc_host + '/webrtc/offer', {
        method: "POST",
        body: JSON.stringify({ sdpOffer, token }),
        headers: { "Content-Type": "application/json" },
    });
    const sdpResponseJson = await sdpResponse.json();

    await peerConnection.setRemoteDescription({
        type: "answer",
        sdp: sdpResponseJson.sdpAnswer,
    });

    return {
        audioEnabled: sdpResponseJson.audioEnabled,
    };
})

/**
 * Enable audio for the chat session
 */
export const enableAudio = createAsyncThunk('enable.audio', async (data, thunkAPI) => {
    if (!peerConnection) {
        return;
    }
    try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
            audio: true
        });
        await transceiver.sender.replaceTrack(mediaStream.getAudioTracks()[0]);

        const message = {
            type: "enable.audio",
        };
        dataChannel.send(JSON.stringify(message));
    } catch (err) {
        return thunkAPI.rejectWithValue({
            error: "Microphone access denied or not available."
        });
    }
})

/**
 * Disable audio for the chat session
 */
export const disableAudio = createAsyncThunk('disable.audio', async (data, thunkAPI) => {
    if (!peerConnection || !mediaStream) {
        return;
    }
    try {
        mediaStream.getAudioTracks().forEach(track => track.stop());
        mediaStream = null;

        const message = {
            type: "disable.audio",
        };
        dataChannel.send(JSON.stringify(message));
    } catch (err) {
        return thunkAPI.rejectWithValue({
            error: "Microphone access denied or not available."
        });
    }
})

/**
 * Close chat and disconnect the session and clean up resources, messages history, etc.
 */
export const closeChat = createAsyncThunk('close.chat', async (data, thunkAPI) => {
    // Mark that the user explicitly closed the chat so ChatContainer's auto-init
    // effect doesn't silently reconnect on the next page navigation. Keep the
    // session id around — when the user opens the chat again we still want to
    // restore the previous conversation via the hibernation/reconnect path.
    sessionStorage.setItem('ai_chat_closed_by_user', '1');
    thunkAPI.dispatch(disconnectChat());
});

/**
 * Start a new chat session by closing the current one and re-initializing.
 */
export const startNewChat = createAsyncThunk('chat.startNew', async (data, thunkAPI) => {
    sessionStorage.removeItem('ai_chat_session_id');
    sessionStorage.removeItem('ai_chat_closed_by_user');
    await thunkAPI.dispatch(disconnectChat());
    cleanupPeerConnection();
    await thunkAPI.dispatch(initChat());
    const context = thunkAPI.getState().chat.context || {};
    thunkAPI.dispatch(fetchSuggestions(context));
});

/**
 * Disconnect the chat session by closing the peer connection and data channel.
 */
export const disconnectChat = createAsyncThunk('disconnect.chat', async (data, thunkAPI) => {
    deltaBatcher.reset();
    if (!peerConnection) {
        return;
    }
    try {
        if (mediaStream) {
            mediaStream.getAudioTracks().forEach(track => track.stop());
            mediaStream = null;
        }
        dataChannel.close();
        peerConnection.close();
        peerConnection = null;
        dataChannel = null;
        transceiver = null;
    } catch (err) {
        return thunkAPI.rejectWithValue({
            error: "Failed to disconnect chat."
        });
    }
})

/**
 * Check if the chat service is available.
 */
export const checkAvailability = createAsyncThunk('chat.checkAvailability', async (data, thunkAPI) => {
    const config = Config.getInstance();
    const response = await fetch(config.host + '/v2/chat-widget-api/is-available');
    return await response.json();
});

/**
 * Rate a specific assistant message (thumbs up/down).
 */
export const rateMessage = createAsyncThunk('chat.rateMessage', async ({ dbId, rating }, thunkAPI) => {
    dataChannel.send(JSON.stringify({ type: 'message.rate', messageId: dbId, rating }));
    thunkAPI.dispatch(chatSlice.actions.setMessageRating({ dbId, rating }));
});

/**
 * Rate the whole chat session (thumbs up/down on the session-level prompt).
 */
export const rateSession = createAsyncThunk('chat.rateSession', async (rating, thunkAPI) => {
    dataChannel.send(JSON.stringify({ type: 'chat.rate', rating }));
    // Only messages from this point onward should be affected by this rating,
    // not the ones already in the log.
    const sinceIndex = thunkAPI.getState().chat.messages.length;
    thunkAPI.dispatch(chatSlice.actions.setChatRatingNegative({ negative: rating === -1, sinceIndex }));
});

/**
 * Counts negative (-1) message ratings among messages up to and including the
 * given index. Used to decide when the inline LiveChatToggler should appear —
 * once this reaches 3, it renders under this message and every bot message
 * after it (the count never decreases within a session).
 */
export const selectNegativeMessageCountUpTo = (messages, index) => {
    let count = 0;
    for (let i = 0; i <= index; i++) {
        if (messages[i]?.rating === -1) count++;
    }
    return count;
};

/**
 * Fetch context-aware action suggestions from the backend.
 */
export const fetchSuggestions = createAsyncThunk('chat.fetchSuggestions', async (context, thunkAPI) => {
    const config = Config.getInstance();
    const response = await axios.post(config.host + '/v2/chat-widget-api/suggest-actions', {
        context: context
    });
    return response.data;
});

/**
 * Export actions and reducer for the chat slice.
 */
export const { handleEvent, postMessage, applyTheme, setInlineMode, toggleSidebarMode, setContext, setMessageRating, setChatRatingNegative } = chatSlice.actions

/**
 * Export reducer for the chat slice.
 */
export const chatReducer = chatSlice.reducer